import React, { useCallback, useState } from "react";
import { UserSearchResult, SimilarUser } from "@/types/user";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/config/axiosConfig";
import { useDebounce } from "@/hooks/use-debounce";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { MagnifyingGlassIcon, UsersIcon } from "@heroicons/react/24/outline";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "@/stores/user/authStore";
import { useToast } from "@/hooks/use-toast";
import { userService } from "@/services/userService";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import { useRateLimitStore } from "@/stores/rateLimitStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Skeleton loading component for user cards
const UserCardSkeleton = () => (
  <div className="flex items-center gap-3 p-2 rounded-md animate-pulse">
    <div className="w-8 h-8 rounded-full bg-neutral-200" />
    <div className="flex-1">
      <div className="h-4 bg-neutral-200 rounded w-24 mb-1" />
      <div className="h-3 bg-neutral-200 rounded w-16" />
    </div>
    <div className="w-16 h-7 rounded-lg bg-neutral-200" />
  </div>
);

const fetchSuggestedUsers = async (userId: string) => {
  const response = await api.social.get(
    `/users/${userId}?include_similar=true`
  );
  return response.data.similar_users;
};

export const FriendsDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingFollows, setPendingFollows] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const { user: currentUser, anonymousUser } = useAuthStore();
  const { toast } = useToast();
  const { setRateLimit } = useRateLimitStore();
  const queryClient = useQueryClient();

  // Use Tanstack Query to fetch and cache suggested users
  const {
    data: suggestedUsers = [] as SimilarUser[],
    isLoading: isLoadingSuggestions,
  } = useQuery({
    queryKey: ["suggestedUsers", currentUser?.user_id],
    queryFn: () =>
      currentUser?.user_id ? fetchSuggestedUsers(currentUser.user_id) : [],
    enabled: !!currentUser?.user_id && isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const debouncedSearch = useDebounce(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.social.get(
        `/search?q=${encodeURIComponent(query)}`
      );
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error("Error searching users:", error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleUserClick = (userId: string) => {
    navigate(`/account/${userId}`);
  };

  const handleFollow = async (user: UserSearchResult | SimilarUser) => {
    // Optimistically update the UI
    setPendingFollows((prev) => new Set(prev).add(user.user_id));

    // Update search results
    setSearchResults((prev) =>
      prev.map((u) =>
        u.user_id === user.user_id
          ? { ...u, is_following: true, is_requested: user.is_private }
          : u
      )
    );

    // Optimistically update the suggestedUsers in the cache
    if (currentUser?.user_id) {
      queryClient.setQueryData(
        ["suggestedUsers", currentUser.user_id],
        (oldData: SimilarUser[] | undefined) => {
          if (!oldData) return [];
          return oldData.map((u) =>
            u.user_id === user.user_id
              ? { ...u, is_following: true, is_requested: user.is_private }
              : u
          );
        }
      );
    }

    const { success, message } = await userService.followUser(user.user_id);
    handleFollowResponse(success, message, user, true);
  };

  const handleUnfollow = async (user: UserSearchResult | SimilarUser) => {
    // Optimistically update the UI
    setPendingFollows((prev) => new Set(prev).add(user.user_id));

    // Update search results
    setSearchResults((prev) =>
      prev.map((u) =>
        u.user_id === user.user_id
          ? { ...u, is_following: false, is_requested: false }
          : u
      )
    );

    // Optimistically update the suggestedUsers in the cache
    if (currentUser?.user_id) {
      queryClient.setQueryData(
        ["suggestedUsers", currentUser.user_id],
        (oldData: SimilarUser[] | undefined) => {
          if (!oldData) return [];
          return oldData.map((u) =>
            u.user_id === user.user_id
              ? { ...u, is_following: false, is_requested: false }
              : u
          );
        }
      );
    }

    const { success, message } = await userService.unfollowUser(user.user_id);
    handleFollowResponse(success, message, user, false);
  };

  const handleFollowResponse = (
    success: boolean,
    message: string,
    user: UserSearchResult | SimilarUser,
    isFollowing: boolean
  ) => {
    setPendingFollows((prev) => {
      const newSet = new Set(prev);
      newSet.delete(user.user_id);
      return newSet;
    });

    if (!success) {
      // Revert search results on failure
      setSearchResults((prev) =>
        prev.map((u) =>
          u.user_id === user.user_id
            ? {
                ...u,
                is_following: !isFollowing,
                is_requested: !isFollowing && user.is_private,
              }
            : u
        )
      );

      // Revert suggestedUsers cache on failure
      if (currentUser?.user_id) {
        queryClient.setQueryData(
          ["suggestedUsers", currentUser.user_id],
          (oldData: SimilarUser[] | undefined) => {
            if (!oldData) return [];
            return oldData.map((u) =>
              u.user_id === user.user_id
                ? {
                    ...u,
                    is_following: !isFollowing,
                    is_requested: !isFollowing && user.is_private,
                  }
                : u
            );
          }
        );
      }

      toast({
        title: isFollowing ? "Follow Request Failed" : "Unfollow Failed",
        description: message,
      });
    } else {
      toast({
        title: isFollowing
          ? user.is_private
            ? "Follow Request Sent"
            : "Following"
          : "Unfollowed",
        description: isFollowing
          ? user.is_private
            ? `You have sent a follow request to ${user.name}`
            : `You are now following ${user.name}`
          : `You have unfollowed ${user.name}`,
      });
    }
  };

  const handleOpen = (open: boolean) => {
    if (anonymousUser) {
      setRateLimit("You must be logged in to follow users.");
      return;
    }
    setIsOpen(open);
  };

  return (
    <CustomDropdown
      isOpen={isOpen}
      setIsOpen={handleOpen}
      title="Find Friends"
      trigger={
        <button className="relative h-10 w-10 2xl:h-11 2xl:w-11 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-neutral-200 rounded-full hover:bg-neutral-50 transition-colors duration-200 shadow-sm focus:outline-none">
          <UsersIcon className="h-5 w-5 2xl:h-[22px] 2xl:w-[22px] text-neutral-700" />
        </button>
      }
      className="w-96 p-3"
    >
      <div className="space-y-4">
        <Input
          type="text"
          placeholder="Search for friends"
          className="text-sm rounded-lg w-full shadow-sm placeholder:font-light"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <ScrollArea className="h-[200px]">
          {isLoading ? (
            <div className="space-y-2">
              {Array(3)
                .fill(0)
                .map((_, index) => (
                  <UserCardSkeleton key={`search-skeleton-${index}`} />
                ))}
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2">
              {searchResults.map((user) => (
                <div
                  key={user.user_id}
                  className="flex items-center gap-3 p-2 hover:bg-neutral-100 rounded-md"
                >
                  <div
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUserClick(user.user_id);
                    }}
                  >
                    <AvatarImage
                      src={user.profile_image}
                      alt={`${user.first_name}'s avatar`}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {user.name}
                        {user.user_id === currentUser?.user_id && (
                          <span className="ml-2 text-xs text-neutral-500">
                            (You)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-neutral-500 font-light">
                        @{user.username}
                      </p>
                    </div>
                  </div>
                  {user.user_id !== currentUser?.user_id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (user.is_following || user.is_requested) {
                          handleUnfollow(user);
                        } else {
                          handleFollow(user);
                        }
                      }}
                      className={`rounded-lg p-2 text-xs ${
                        pendingFollows.has(user.user_id)
                          ? "bg-neutral-100"
                          : user.is_following || user.is_requested
                          ? "bg-white hover:bg-neutral-100"
                          : "bg-white hover:bg-neutral-100"
                      } border border-neutral-200 transition-colors duration-200 flex items-center gap-2`}
                      disabled={pendingFollows.has(user.user_id)}
                    >
                      {pendingFollows.has(user.user_id) ? (
                        <Loader2 className="h-3 w-3 animate-spin text-indigo-500" />
                      ) : user.is_following || user.is_requested ? (
                        user.is_requested ? (
                          "Requested"
                        ) : (
                          "Following"
                        )
                      ) : (
                        "Follow"
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : searchQuery ? (
            <div className="flex items-center justify-center py-4 text-neutral-500">
              <UsersIcon className="h-5 w-5 mr-2 opacity-50" />
              <span className="text-sm">
                No users found matching "{searchQuery}"
              </span>
            </div>
          ) : isLoadingSuggestions ? (
            <div className="space-y-4">
              <div className="px-2 text-sm font-medium text-neutral-700">
                Suggested Profiles
              </div>
              <div className="space-y-2">
                {Array(4)
                  .fill(0)
                  .map((_, index) => (
                    <UserCardSkeleton key={`suggestion-skeleton-${index}`} />
                  ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="px-2 text-sm font-medium text-neutral-700">
                Suggested Profiles
              </div>
              <div className="space-y-2">
                {suggestedUsers.map((user: SimilarUser) => (
                  <div
                    key={user.user_id}
                    className="flex items-center gap-3 p-2 hover:bg-neutral-100 rounded-md"
                  >
                    <div
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUserClick(user.user_id);
                      }}
                    >
                      <AvatarImage
                        src={user.profile_image}
                        alt={`${user.name}'s avatar`}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {user.name}
                          {user.user_id === currentUser?.user_id && (
                            <span className="ml-2 text-xs text-neutral-500">
                              (You)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-neutral-500 font-light">
                          @{user.username}
                        </p>
                      </div>
                    </div>
                    {user.user_id !== currentUser?.user_id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (user.is_following || user.is_requested) {
                            handleUnfollow(user);
                          } else {
                            handleFollow(user);
                          }
                        }}
                        className={`rounded-lg p-2 text-xs ${
                          pendingFollows.has(user.user_id)
                            ? "bg-neutral-100"
                            : user.is_following || user.is_requested
                            ? "bg-white hover:bg-neutral-100"
                            : "bg-white hover:bg-neutral-100"
                        } border border-neutral-200 transition-colors duration-200 flex items-center gap-2`}
                        disabled={pendingFollows.has(user.user_id)}
                      >
                        {pendingFollows.has(user.user_id) ? (
                          <Loader2 className="h-3 w-3 animate-spin text-indigo-500" />
                        ) : user.is_following || user.is_requested ? (
                          user.is_requested ? (
                            "Requested"
                          ) : (
                            "Following"
                          )
                        ) : (
                          "Follow"
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>
    </CustomDropdown>
  );
};

export default FriendsDropdown;
